import axios from 'axios';
import { apiService } from './api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthUser {
  id: number;
  email: string;
  username: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  companyId: number;
  controllerId?: number;
  preferredLanguage?: string;
  avatarPath?: string;
  access?: string;
}

export interface AuthTokens {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  userId: number;
  email: string;
  username: string;
  firstName?: string;
  lastName?: string;
}

const isDev = import.meta.env.DEV;

class AuthService {
  private readonly TOKEN_KEY = 'access_token';
  private readonly USER_KEY = 'current_user';
  private refreshTimer?: NodeJS.Timeout;
  private isInitialized = false;
  private isInitializing = false;
  private initPromise: Promise<boolean> | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;
  private lastRefreshAttempt = 0;
  private readonly REFRESH_COOLDOWN = 5000; // 5 seconds cooldown between refresh attempts

  // In-memory caches to avoid repeated storage access and redundant requests
  private tokenCache: string | null = null;
  private userCache: AuthUser | null = null;
  private lastUserFetch = 0;
  private readonly USER_CACHE_TTL = 5000; // 5 seconds
  private currentUserPromise: Promise<AuthUser> | null = null; // single-flight

  constructor() {
    // Bind methods to ensure proper context
    this.initializeAuth = this.initializeAuth.bind(this);
    this.refreshToken = this.refreshToken.bind(this);

    // Initialize caches from storage if available
    try {
      this.tokenCache = localStorage.getItem(this.TOKEN_KEY) || localStorage.getItem('token');
      const userStr = localStorage.getItem(this.USER_KEY);
      this.userCache = userStr ? JSON.parse(userStr) : null;
    } catch {
      this.tokenCache = null;
      this.userCache = null;
    }

    // Keep caches in sync with other tabs/windows
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e: StorageEvent) => {
        if (e.key === this.TOKEN_KEY || e.key === 'token') {
          this.tokenCache = e.newValue;
        }
        if (e.key === this.USER_KEY) {
          try {
            this.userCache = e.newValue ? JSON.parse(e.newValue) : null;
          } catch {
            this.userCache = null;
          }
        }
      });
    }
  }

  async initializeAuth(): Promise<boolean> {
    if (this.isInitialized) {
      return this.isAuthenticated();
    }

    // If already initializing, return the existing promise
    if (this.isInitializing && this.initPromise) {
      return this.initPromise;
    }

    this.isInitializing = true;
    this.initPromise = this.doInitializeAuth();
    
    try {
      const result = await this.initPromise;
      return result;
    } finally {
      this.isInitializing = false;
      this.initPromise = null;
    }
  }

  private async doInitializeAuth(): Promise<boolean> {
    const token = this.getToken();
    if (token) {
      try {
        // Verify token is still valid by getting current user
        // Use skipRefresh to prevent refresh loop during initialization
        await this.getCurrentUser(true);
        // Start refresh timer
        this.startRefreshTimer();
        this.isInitialized = true;
        return true;
      } catch (error: any) {
        // If it's an aborted request, don't clear auth - just return current state
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
          if (isDev) console.warn('Auth initialization request was aborted');
          this.isInitialized = true;
          return this.isAuthenticated();
        }
        // Token is invalid, clear it
        this.clearAuth();
        this.isInitialized = true;
        return false;
      }
    }
    this.isInitialized = true;
    return false;
  }

  async login(credentials: LoginCredentials): Promise<AuthUser> {
    try {
      const response = await apiService.axiosInstance.post<AuthTokens>("/api/v1/user-auth/login", credentials, {
        withCredentials: true // Important for cookies
      });

      // Map backend snake_case to camelCase with fallbacks
      const rawData: any = response.data || {};
      const accessToken: string | undefined = rawData.accessToken ?? rawData.access_token;
      const userId: number | undefined = rawData.userId ?? rawData.user_id;
      const email: string | undefined = rawData.email;
      const username: string | undefined = rawData.username;
      const firstName: string | undefined = rawData.firstName ?? rawData.first_name;
      const lastName: string | undefined = rawData.lastName ?? rawData.last_name;

      if (!accessToken) {
        throw new Error('Login response does not contain an access token');
      }

      // Store token
      this.setToken(accessToken);

      // Store user info (partial until we fetch /me)
      const user: AuthUser = {
        id: userId || 0,
        email: email || '',
        username: username || '',
        firstName,
        lastName,
        isActive: true,
        companyId: 0, // Will be updated from /me endpoint
        controllerId: undefined,
        preferredLanguage: undefined,
        avatarPath: undefined
      };
      // Save a partial user so UI can optimistically update; will be overwritten by /me
      this.setUser(user);

      // Mark as initialized after successful login
      this.isInitialized = true;

      // Get full user details
      const fullUser = await this.getCurrentUser();
      this.setUser(fullUser);

      // Start token refresh timer
      this.startRefreshTimer();

      return fullUser;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Invalid email or password');
      } else if (error.response?.status === 403) {
        throw new Error('Account is locked');
      }
      throw new Error('Login failed. Please try again.');
    }
  }

  /**
   * Lightweight public registration used by marketing pages.
   * Falls back gracefully if the backend endpoint is not available.
   */
  async register(email: string, password: string): Promise<void> {
    try {
      await apiService.axiosInstance.post("/api/v1/user-auth/register", { email, password }, {
        withCredentials: true
      });
    } catch (error: any) {
      // Backend may not expose registration; log in dev and swallow to keep UX smooth
      if (isDev) console.warn('Registration endpoint unavailable or failed', error?.message || error);
    }
  }

  async logout(): Promise<void> {
    // Clear auth state first to prevent any race conditions
    this.clearAuth();
    
    try {
      await apiService.axiosInstance.post("/api/v1/user-auth/logout", {}, {
        withCredentials: true
      });
    } catch (error) {
      if (isDev) console.error('Logout error:', error);
    }
  }

  async getCurrentUser(skipRefresh = false): Promise<AuthUser> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No authentication token');
    }

    // Serve from cache if fresh
    if (this.userCache && Date.now() - this.lastUserFetch < this.USER_CACHE_TTL) {
      return this.userCache;
    }

    // Single-flight: if a request is in-flight, reuse it
    if (this.currentUserPromise) {
      return this.currentUserPromise;
    }

    this.currentUserPromise = (async () => {
      try {
        const response = await apiService.axiosInstance.get<any>("/api/v1/user-auth/me");

        // Map backend response (snake_case) to frontend interface (camelCase)
        const rawUser = response.data;
        const user: AuthUser = {
          id: rawUser.id,
          email: rawUser.email,
          username: rawUser.username,
          firstName: rawUser.first_name,
          lastName: rawUser.last_name,
          isActive: rawUser.is_active,
          companyId: rawUser.company_id || 0,
          controllerId: rawUser.controller_id,
          preferredLanguage: rawUser.preferred_language,
          avatarPath: rawUser.avatar_path,
          access: rawUser.access
        };

        this.setUser(user);
        this.lastUserFetch = Date.now();
        return user;
      } catch (error: any) {
        // Check if it's a 401 and we haven't already tried to refresh
        if (error.response?.status === 401 && !skipRefresh && !this.isRefreshing) {
          const refreshed = await this.refreshToken();
          if (refreshed) {
            // Retry getting user with skipRefresh to prevent infinite loop
            return this.getCurrentUser(true);
          }
        }
        
        // If it's a network error (aborted request), return cached user if available
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
          if (isDev) console.warn('Request was aborted, likely due to navigation');
          const cachedUser = this.getUser();
          if (cachedUser) {
            return cachedUser;
          }
        }
        
        throw error;
      } finally {
        this.currentUserPromise = null;
      }
    })();

    return this.currentUserPromise;
  }

  async refreshToken(): Promise<boolean> {
    // Check cooldown to prevent rapid refresh attempts
    const now = Date.now();
    if (now - this.lastRefreshAttempt < this.REFRESH_COOLDOWN) {
      if (isDev) console.warn('Refresh attempt blocked due to cooldown');
      return false;
    }

    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.lastRefreshAttempt = now;
    this.refreshPromise = this.doRefreshToken();
    
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefreshToken(): Promise<boolean> {
    try {
      const response = await apiService.axiosInstance.post<AuthTokens>("/api/v1/user-auth/refresh", {}, {
        withCredentials: true,
        timeout: 10000
      });

      const rawData: any = response.data || {};
      const accessToken: string | undefined = rawData.accessToken ?? rawData.access_token;
      
      if (!accessToken) {
        throw new Error('Refresh response does not contain an access token');
      }
      
      this.setToken(accessToken);
      
      // Restart refresh timer
      this.startRefreshTimer();
      
      return true;
    } catch (error: any) {
      // Don't log aborted requests as errors
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
        if (isDev) console.warn('Token refresh request was aborted');
        return false;
      }
      
      if (isDev) console.error('Token refresh failed:', error);
      
      // Only clear auth if it's a definitive failure (not a network error)
      if (error.response?.status === 401 || error.response?.status === 403) {
        this.clearAuth();
      }
      
      return false;
    }
  }

  private startRefreshTimer() {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Only start timer if we're authenticated
    if (!this.isAuthenticated()) {
      return;
    }

    // Refresh token 5 minutes before expiry (25 minutes)
    this.refreshTimer = setTimeout(() => {
      // Check if still authenticated before refreshing
      if (this.isAuthenticated()) {
        this.refreshToken();
      }
    }, 25 * 60 * 1000);
  }

  private clearAuth() {
    // Clear timer first
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
    
    // Clear storage
    try {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
      localStorage.removeItem('token'); // Clear old token key too
    } catch {}
    
    // Update axios default header
    delete axios.defaults.headers.common['Authorization'];
    
    // Reset caches and flags
    this.tokenCache = null;
    this.userCache = null;
    this.lastUserFetch = 0;
    this.isInitialized = false;
    this.isRefreshing = false;
    this.refreshPromise = null;
  }

  getToken(): string | null {
    if (this.tokenCache !== null) return this.tokenCache;
    try {
      const token = localStorage.getItem(this.TOKEN_KEY) || localStorage.getItem('token');
      this.tokenCache = token;
      return token;
    } catch {
      return null;
    }
  }

  private setToken(token: string) {
    this.tokenCache = token;
    try {
      localStorage.setItem(this.TOKEN_KEY, token);
      localStorage.setItem('token', token); // Keep compatibility with existing code
    } catch {}
    
    // Update axios default header (kept for compatibility with legacy direct axios usage)
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  getUser(): AuthUser | null {
    if (this.userCache) return this.userCache;
    const userStr = localStorage.getItem(this.USER_KEY);
    if (!userStr) return null;
    
    try {
      this.userCache = JSON.parse(userStr);
      return this.userCache;
    } catch {
      return null;
    }
  }

  private setUser(user: AuthUser) {
    this.userCache = user;
    this.lastUserFetch = Date.now();
    try {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    } catch {}
  }

  isAuthenticated(): boolean {
    return !!this.getToken() && !!this.getUser();
  }

  hasRole(role: string | string[]): boolean {
    const user = this.getUser();
    if (!user || !user.access) {
      return false;
    }
    if (Array.isArray(role)) {
      return role.includes(user.access);
    }
    return user.access === role;
  }

  canAccess(_resource: string): boolean {
    // Basic permission checking - can be extended based on requirements
    return this.isAuthenticated();
  }

  isAdmin(): boolean {
    return this.hasRole('admin');
  }

  isManager(): boolean {
    return this.hasRole('manager');
  }
}

export const authService = new AuthService();
export default authService;
